import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

/**
 * Popup — renders children in a fixed-position overlay via React portal.
 * Always on top (z-index 9999), never clipped by parent overflow.
 *
 * Props:
 *   anchorRect  — DOMRect from element.getBoundingClientRect()
 *   onClose     — called when backdrop or × is clicked
 *   width       — popup width in px (default 280)
 *   children    — popup content
 */
export default function Popup({ anchorRect, onClose, width = 280, children }) {
  const popupRef = useRef(null);

  // Calculate position: prefer below the anchor, flip above if too close to bottom
  const MARGIN = 8;
  const ARROW  = 10;

  let top  = anchorRect.bottom + ARROW + MARGIN;
  let left = anchorRect.left + anchorRect.width / 2 - width / 2;

  // Clamp horizontally so it doesn't go off-screen
  const vw = window.innerWidth;
  if (left < MARGIN)          left = MARGIN;
  if (left + width > vw - MARGIN) left = vw - width - MARGIN;

  // Flip above if popup would go below viewport
  const vh = window.innerHeight;
  const estimatedHeight = 300;
  let flipAbove = false;
  if (top + estimatedHeight > vh - MARGIN) {
    top = anchorRect.top - estimatedHeight - ARROW - MARGIN;
    flipAbove = true;
  }

  // Arrow horizontal position relative to popup
  const arrowLeft = Math.max(12, Math.min(
    anchorRect.left + anchorRect.width / 2 - left - 7,
    width - 24
  ));

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return ReactDOM.createPortal(
    <>
      {/* Invisible backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
      />

      {/* Popup box */}
      <div
        ref={popupRef}
        onClick={e => e.stopPropagation()}
        style={{
          position: 'fixed',
          top,
          left,
          width,
          zIndex: 9999,
          background: '#fff',
          borderRadius: 10,
          boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          border: '1px solid #D0D7DE',
          animation: 'popup-in 0.15s ease',
        }}
      >
        <style>{`
          @keyframes popup-in {
            from { opacity: 0; transform: translateY(${flipAbove ? '6px' : '-6px'}) scale(0.97); }
            to   { opacity: 1; transform: none; }
          }
        `}</style>

        {/* Arrow */}
        <div style={{
          position: 'absolute',
          [flipAbove ? 'bottom' : 'top']: -7,
          left: arrowLeft,
          width: 13, height: 13,
          background: '#fff',
          border: '1px solid #D0D7DE',
          borderBottom: flipAbove ? '1px solid #D0D7DE' : 'none',
          borderRight:  flipAbove ? 'none' : '1px solid #D0D7DE',
          borderTop:    flipAbove ? 'none' : '1px solid #D0D7DE',
          borderLeft:   flipAbove ? '1px solid #D0D7DE' : 'none',
          transform: `rotate(${flipAbove ? '225deg' : '45deg'})`,
        }} />

        {children}
      </div>
    </>,
    document.body
  );
}
